import { Button, Card, Dropdown, Menu, Space, Spin } from 'antd';
import React, { useState, useEffect, useRef } from 'react';
import { Company } from '../../../models/company-interface';
import { CompanySettings } from '../../../models/company-settings-interface';
import { SupportTier } from '../../../models/support-tier-interface';
import { MeetingSettings } from '../../../models/meeting-settings-interface';
import { TimeZone } from '../../../models/time-zone-interface';
import Support from './Support';
import SubscriptionDisplay from './SubscriptionDisplay';
import FeaturesList from './FeaturesList';
import TimeZoneDisplay from './TimeZoneDisplay';
import DemoCompany from './DemoCompany';
import { Subscription } from '../../../models/subscription-interface';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import ImportUsersModal from '../../../components/modals/ImportUsersModal';
import { CompanyQueries } from '../../../queries/company-queries';
import { CompanyProductVersionModel, Feature } from '../../../models/product-interface';
import SupportTierChangeModal from '../../../components/modals/SupportTierChangeModal';
import SSOConfigModal from '../../../components/modals/SSOConfigModal';
import { HasRight, Rights } from '../../../utilites/auth';
import { UserQueries } from '../../../queries/user-queries';
import { SSO_CONFIG_MODES, SSOConfigMode } from '../../../constants/sso-config-modes';

interface FeaturesCardProps {
  companyDetails: Company | undefined;
  supportDetails: SupportTier | undefined;
  companyDocumentSettings: CompanySettings[] | undefined;
  companyMeetingSettings: MeetingSettings | undefined;
  companyLogo: string | undefined;
  timeZones: TimeZone[] | undefined;
  isLoading: boolean;
  canUpdate: boolean;
  subscription: Subscription | undefined;
  products: CompanyProductVersionModel[] | undefined;
  demoFeatures: Feature[] | undefined;
}

export default function FeaturesCard({
  supportDetails,
  companyDetails,
  companyDocumentSettings,
  companyMeetingSettings,
  companyLogo,
  timeZones,
  isLoading,
  canUpdate,
  subscription,
  products,
  demoFeatures
}: FeaturesCardProps) {
  const [importUsersModalVisible, setImportUsersModalVisible] = useState(false);
  const [supportTierModalVisible, setSupportTierModalVisible] = useState(false);
  const [ssoConfigModalVisible, setSSOConfigModalVisible] = useState(false);
  const [ssoConfigMode, setSSOConfigMode] = useState<SSOConfigMode>(SSO_CONFIG_MODES.ADD_MEMBER_APP);
  const [ssoConfigAddLoading, setSSOConfigAddLoading] = useState(false);

  const updateCompanyAccessMutation = CompanyQueries.useUpdateCompanyAccess(companyDetails?.id!);

  const exportOnboardUsersMutator = UserQueries.useOnboardUserExport(companyDetails?.id!);
  const isAdmin: boolean = HasRight([Rights.CSAdmin]);

  const prevSSOConfigRef = useRef({
    admin: companyDetails?.singleSignOnConfiguredForAdmin,
    memberApp: companyDetails?.singleSignOnConfiguredForMemberApp
  });

  useEffect(() => {
    const currentAdmin = companyDetails?.singleSignOnConfiguredForAdmin;
    const currentMemberApp = companyDetails?.singleSignOnConfiguredForMemberApp;
    const prevAdmin = prevSSOConfigRef.current.admin;
    const prevMemberApp = prevSSOConfigRef.current.memberApp;

    if (ssoConfigAddLoading && (currentAdmin !== prevAdmin || currentMemberApp !== prevMemberApp)) {
      setSSOConfigAddLoading(false);
    }

    prevSSOConfigRef.current = {
      admin: currentAdmin,
      memberApp: currentMemberApp
    };
  }, [companyDetails?.singleSignOnConfiguredForAdmin, companyDetails?.singleSignOnConfiguredForMemberApp, ssoConfigAddLoading]);

  const actionMenu = (
    <Menu onClick={() => {}}>
      <Menu.Item
        key='importUsers'
        onClick={() => setImportUsersModalVisible(true)}
      >
        Import Users
      </Menu.Item>
      {isAdmin && companyDetails?.singleSignOnAvailable && (
        <>
          <Menu.Item
            key={companyDetails?.singleSignOnConfiguredForAdmin ? 'editAdminSSOConfig' : 'addAdminSSOConfig'}
            onClick={() => {
              setSSOConfigMode(companyDetails?.singleSignOnConfiguredForAdmin ? SSO_CONFIG_MODES.EDIT_ADMIN: SSO_CONFIG_MODES.ADD_ADMIN);
              setSSOConfigModalVisible(true);
            }}
          >
            {companyDetails?.singleSignOnConfiguredForAdmin ? 'Edit' : 'Add'} Admin SSO Config
          </Menu.Item>
          <Menu.Item
            key={companyDetails?.singleSignOnConfiguredForMemberApp ? 'editMemberAppSSOConfig' : 'addMemberAppSSOConfig'}
            onClick={() => {
              setSSOConfigMode(companyDetails?.singleSignOnConfiguredForMemberApp ? SSO_CONFIG_MODES.EDIT_MEMBER_APP : SSO_CONFIG_MODES.ADD_MEMBER_APP);
              setSSOConfigModalVisible(true);
            }}
          >
            {companyDetails?.singleSignOnConfiguredForMemberApp ? 'Edit' : 'Add'} Member App SSO Config
          </Menu.Item>
        </>
      )}
      <Menu.Item
        key='supportTier'
        onClick={() => setSupportTierModalVisible(true)}
        disabled={!isAdmin}
      >
        Change Support Tier
      </Menu.Item>
      <Menu.Item
        key='exportOnboardUsers'
        onClick={() => exportOnboardUsers()}
        disabled={!isAdmin}
      >
        Export Onboard Mapped User List
      </Menu.Item>
      {canUpdate && 
      <Menu.Item
        key='disableEnableCompany'
        onClick={async () =>
          await updateCompanyAccessMutation.mutateAsync(
            !companyDetails?.confirmed
          )
        }
        className={companyDetails?.confirmed ? 'red-text' : ''}
      >
        {`${companyDetails?.confirmed ? 'Disable' : 'Enable'} Company`}
      </Menu.Item>
      }
    </Menu>
  );

  const allFeatures = ():Feature[] =>  {
    const features:Feature[] = []
    products?.forEach(p => {
        p.features.forEach(pf => {
            if(!features.some(f => f.id === pf.id)) {
                features.push(pf)
            } else {
                const existing = features.find(f => f.id === pf.id)
                
                if(existing && existing.recordLimit > 0 && (pf.recordLimit === 0 || pf.recordLimit > existing.recordLimit)) {
                    if(pf.recordLimit !== 0 && existing.recordLimit !== 0) {
                        existing.recordLimit = existing.recordLimit + pf.recordLimit
                    } else {
                        existing.recordLimit = pf.recordLimit
                    }
                }
            }
        })
    })

    return features
  }

  const exportOnboardUsers = () => {
    exportOnboardUsersMutator.mutateAsync();
  }

  return (
    <>
      <Card>
        <Spin spinning={isLoading || ssoConfigAddLoading}>
          <SubscriptionDisplay
            companyDetails={companyDetails}
            companyLogo={companyLogo}
            subscription={subscription}
            isLoading={isLoading}
          />
          <Support supportDetails={supportDetails} />
          <TimeZoneDisplay
            companyDetails={companyDetails}
            timeZones={timeZones}
          />
          <FeaturesList
            companyDetails={companyDetails}
            companyDocumentSettings={companyDocumentSettings}
            companyMeetingSettings={companyMeetingSettings}
            features={allFeatures()}
          />
          <DemoCompany companyDetails={companyDetails} canUpdate={canUpdate} />
          {!!companyDetails && (
            <div style={{ marginTop: 16 }}>
              <Dropdown overlay={actionMenu}>
                <Button
                  type='primary'
                  onClick={(e) => e.preventDefault()}
                  loading={updateCompanyAccessMutation.isLoading}
                >
                  <Space>
                    Action
                    <FontAwesomeIcon icon={faAngleDown as IconProp} />
                  </Space>
                </Button>
              </Dropdown>
            </div>
          )}
        </Spin>
      </Card>
      <ImportUsersModal
        companyId={companyDetails?.id!}
        isVisible={importUsersModalVisible}
        handleClose={() => setImportUsersModalVisible(false)}
      />
      {isAdmin && (
        <SupportTierChangeModal
          isModalVisible={supportTierModalVisible}
          setIsModalVisible={setSupportTierModalVisible}
          companyId={companyDetails?.id!}
          currentTierId={supportDetails?.id ?? 0}
        />
      )}
      {isAdmin && (
        <SSOConfigModal
          isModalVisible={ssoConfigModalVisible}
          setIsModalVisible={setSSOConfigModalVisible}
          companyId={companyDetails?.id!}
          mode={ssoConfigMode}
          isConfigured={companyDetails?.singleSignOnConfigured ?? false}
          onAddSuccess={() => {
            if (ssoConfigMode === SSO_CONFIG_MODES.ADD_ADMIN || ssoConfigMode === SSO_CONFIG_MODES.ADD_MEMBER_APP) {
              setSSOConfigAddLoading(true);
            }
          }}
        />
      )}
    </>
  );
}
